import Flex from '@react-css/flex';
import Spinner from 'components/Common/Spinner';
import { SettingsDescription, SettingsSectionTitle } from 'containers/SettingsPage/SettingsLayout';
import useAlertQueue from 'hooks/useAlertQueue';
import { useBalanceBuyerPortal } from 'hooks/useBalanceRedirect';
import { useEffect } from 'react';
import { Badge } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'store';
import { selectCompany } from 'store/selectors/me/company';
import { refreshMe } from 'store/slices/me';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';

const BalanceBuyerPaymentMethod = () => {
  const dispatch = useAppDispatch();
  const alertQueue = useAlertQueue();
  const company = useAppSelector(selectCompany);
  const { openPortal, isRedirecting } = useBalanceBuyerPortal();
  const verified = !!company?.balance?.paymentMethodVerified;

  // Balance onboarding happens on a redirected page, so refresh company state when the tab
  // regains focus — until the payment method is verified, at which point the badge stops
  // lying about stale status.
  useEffect(() => {
    if (verified) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(refreshMe());
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [dispatch, verified]);

  const onClick = () => {
    openPortal().catch((err) => {
      alertQueue.addErrorAlert(
        'Something went wrong',
        (err as Error)?.message ?? 'Failed to open Balance portal'
      );
    });
  };

  const buttonLabel = verified
    ? 'Manage Balance Payment Method'
    : 'Add payment method with Balance';

  return (
    <>
      <SettingsSectionTitle>
        Balance
        {verified && (
          <Badge variant="success" pill className="ml-3">
            Connected
          </Badge>
        )}
      </SettingsSectionTitle>
      <SettingsDescription>
        {verified
          ? 'Your Balance payment method is connected. Manage your payment method through the Balance portal.'
          : 'Pay invoices via credit card or ACH with Balance. Add a payment method through the Balance portal.'}
      </SettingsDescription>
      <Flex>
        <PrimaryButton size="small" onClick={onClick} disabled={isRedirecting}>
          {isRedirecting ? <Spinner color="primary" className="mx-4" small /> : buttonLabel}
        </PrimaryButton>
      </Flex>
    </>
  );
};

export default BalanceBuyerPaymentMethod;
